// @flow

import styled, { css, keyframes } from "styled-components";
import { fontBold, fontCondensed, fontNormal } from "@fdr/static-ui/Typography";
import { FilterButtonBar } from "@tvg/design-system/web";
import buildColor from "@fdr/static-ui/ColorPalette";
import { buildText } from "../../_atoms/Text";
import Button from "../../_atoms/Button";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    left: 200px;
  }

  to {
    opacity: 1;
    left: 0;
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    left: 0;
  }

  to {
    opacity: 0;
    left: 200px;
  }
`;

export const RaceCardSection = styled.section`
  width: 100%;
  flex: 1 1 auto;
  background-color: ${buildColor("grey", "100")};
  padding-top: ${(props) =>
    props.isHandicapFixed && `${props.handicapFormatHeight}px`};
  ${({ isPick }) =>
    isPick &&
    css`
      & > div > div > div > div:last-child > div {
        width: calc(100% - 12px);
      }
    `};
`;

export const RaceCardWrapper = styled.div`
  ${(props) =>
    !props.isLeg &&
    css`
      border-radius: 2px;
    `}
`;

export const RaceCardTable = styled.div`
  width: 100%;
  ${(props) =>
    props.isLeg &&
    css`
      border-radius: 4px;
    `}

  & > :last-child .hasBottomBorder::after {
    display: none;
  }
`;

export const NumberFireTable = styled.div`
  width: 100%;
  background-color: white;
  ${(props) =>
    props.isLeg &&
    css`
      border-radius: 4px;
    `}

  & > :last-child .hasBottomBorder::after {
    display: none;
  }
`;

export const NumberFireTitle = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 0 8px 12px;
  color: ${buildColor("navy", "700")};
  font-family: ${fontBold};
  font-size: 18px;

  & > span {
    height: 20px;
    padding-left: 4px;
  }

  ${({ isRAS }) =>
    !isRAS &&
    css`
      & > svg {
        height: 16px;
        width: 72px;
      }
    `}
`;

export const RaceCardLegWrapper = styled.div`
  ${({ isNewLayout, isSingle }) =>
    isNewLayout
      ? css`
          width: calc(100% - 28px);
          padding: 12px 4px;

          &:first-child {
            ${!isSingle &&
            css`
              padding-left: 0;
              width: calc(100% - 32px);
            `}
          }

          &:last-child {
            ${isSingle
              ? css`
                  width: 100%;
                  padding: 12px 16px;
                `
              : css`
                  padding-right: 0;
                `}
          }
        `
      : css`
          width: calc(100% - 16px);
          padding: 12px 0 12px 8px;

          &:last-child {
            width: 100%;
            ${isSingle
              ? css`
                  padding: 12px 16px;
                `
              : css`
                  padding-left: 16px;
                `}
          }
        `}
  ${({ onboardingInfo }) => onboardingInfo && "padding-bottom: 70px"}
  ${({ isPick }) =>
    isPick &&
    css`
      padding-top: 0;
    `}
`;

export const LegCard = styled.div`
  ${(props) =>
    props.isLeg &&
    css`
      border-radius: 4px;
      border: 1px solid ${buildColor("grey", "300")};
    `}
  ${(props) =>
    props.numberFireLegCard &&
    css`
      margin-top: 8px;
    `}
`;

export const Text = styled(
  buildText({
    tag: "span",
    fontSize: 10,
    color: "#5c9de0",
    uppercase: true,
    bold: true
  })
)`
  height: 11px;
`;

export const BetAllModalWrapper = styled.div`
  visibility: ${({ isBetAllOpened }) =>
    isBetAllOpened ? "visible" : "hidden"};
  height: 0;
  position: relative;
  z-index: 1;
  ${({ isBetAllOpened }) =>
    isBetAllOpened
      ? css`
          animation: ${fadeIn} 0.8s linear;
          transition: visibility 0.8s linear;
        `
      : css`
          animation: ${fadeOut} 0.8s ease-out;
          animation-delay: ${({ numColumns }) => 0.09 * numColumns}s;
          transition: visibility 0.8s ease-out;
        `}
`;

export const BetAllModal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${(props) => props.headerHeight}px;
  background-color: white;
  overflow: hidden;
`;

export const CloseButton = styled.div`
  width: 48px;
  height: 40px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  ${({ isBetAllOpened }) =>
    isBetAllOpened
      ? css`
          animation: ${slideIn} 0.5s ease-in;
        `
      : css`
          animation: ${slideOut} 0.5s ease-in;
          animation-delay: ${({ numColumns }) => 0.09 * numColumns}s;
        `}
`;

export const LegInfoContainer = styled.div`
  display: flex;
  border-radius: 4px;
  align-items: center;
  background-color: ${buildColor("white", "100")};
`;

export const LegInfoWrapper = styled.dl`
  width: 100%;
  margin: ${({ isNewLayout }) => (isNewLayout ? "12px 0 12px 12px" : "12px")};
`;

export const RaceNumber = styled.dt`
  font-family: ${fontBold};
  font-weight: 500;
  margin-bottom: ${({ isNewLayout }) => (isNewLayout ? "12px" : "4px")};
  font-size: 16px;
  color: ${buildColor("black", "100")};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const LegTitle = styled.div`
  span {
    font-family: ${fontNormal};
    margin-left: 8px;
    vertical-align: baseline;
  }
`;

export const LegInfoIconsWrapper = styled.div`
  display: flex;
  margin-right: 12px;

  button,
  a {
    margin-left: 20px;
  }

  & > *:first-child {
    margin-left: 0;
  }
`;

export const LegInfoIconButton = styled.button`
  position: relative;
  height: 16px;
  background-color: transparent;
  outline: none;
  cursor: pointer;
  ${({ on }) =>
    on &&
    css`
      &::before {
        content: "";
        position: absolute;
        top: -1px;
        right: -1px;
        width: 6px;
        height: 6px;
        background-color: ${buildColor("red", "500")};
        border: 1px solid ${buildColor("white", "100")};
        border-radius: 50%;
      }
    `};
`;

export const NumberFireInfo = styled.div`
  padding: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const NumberFireIcon = styled.div`
  margin-right: 3px;
  margin-left: 3px;

  svg {
    width: 58px;
    height: 14px;
  }
`;

export const NumberFireText = styled.span`
  display: flex;
  justify-content: center;
  align-items: ${({ isRAS }) => (isRAS ? "center" : "flex-start")};
  color: ${buildColor("grey", "800")};
  font-family: ${fontNormal};
  font-size: 12px;
`;

export const FixedMessageContainer = styled.div`
  pointer-events: none;
  display: flex;
  position: fixed;
  width: 100%;
  bottom: calc(env(safe-area-inset-bottom) + 60px);
  z-index: 11;
  padding-left: 12px;
  padding-right: 12px;
  ${({ isBettingModalOpen }) =>
    isBettingModalOpen &&
    css`
      position: absolute;
      top: -65px;
      display: block;
    `}
`;

export const InlineMessageWrapper = styled.div`
  border-top: 1px solid ${buildColor("grey", "300")};
`;

export const InlineMessageButtons = styled.div`
  display: flex;
  margin-top: 16px;

  & > a {
    flex: 1;

    &:first-of-type:not(:only-of-type) {
      margin-right: 12px;
    }
  }
`;

export const FloatingBannerWrapper = styled.div`
  margin: 12px 16px 0;
`;

export const VideoSectionWrapper = styled.div`
  margin-top: 16px;
  > section {
    border: 1px solid ${buildColor("grey", "L2")};
    border-radius: 4px;
  }
`;

export const LegNavigatorContainer = styled.div`
  z-index: 20;
  position: sticky;
  top: 173px;
  background-color: ${buildColor("grey", "100")};
  padding: 12px 16px;
`;

export const FilterBarWrapper = styled(FilterButtonBar)`
  display: flex;
  overflow-x: scroll;

  button {
    flex: 1;
    min-width: 52px;
  }
`;
